'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Plus, Trash2, ToggleLeft, ToggleRight, Mail, ChevronDown, ChevronUp, Save, AlertTriangle, Info, Zap, X, Check } from 'lucide-react';
import {
  AlertThreshold, ThresholdCondition, AlertSeverity,
  getAlertThresholds, createAlertThreshold, updateAlertThreshold,
  deleteAlertThreshold, DEFAULT_THRESHOLD_TEMPLATES, METRIC_LABELS, CONDITION_LABELS
} from '@/lib/services/alertThresholdService';
import { useAuth } from '@/contexts/AuthContext';

interface AlertThresholdsConfigProps {
  patientId?: string;
  patientName?: string;
  onClose?: () => void;
}

const METRICS = Object.keys(METRIC_LABELS);
const CONDITIONS: ThresholdCondition[] = ['gt', 'lt', 'drop_pct', 'rise_pct'];
const SEVERITIES: AlertSeverity[] = ['info', 'warning', 'critical'];

const severityStyle: Record<AlertSeverity, { bg: string; text: string; border: string; icon: React.ReactNode }> = {
  info: { bg: 'bg-sky-950/30', text: 'text-sky-400', border: 'border-sky-800/40', icon: <Info size={11} /> },
  warning: { bg: 'bg-amber-950/30', text: 'text-amber-400', border: 'border-amber-800/40', icon: <AlertTriangle size={11} /> },
  critical: { bg: 'bg-red-950/30', text: 'text-red-400', border: 'border-red-800/40', icon: <Zap size={11} /> },
};

const emptyForm = (): Omit<AlertThreshold, 'id' | 'clinician_id' | 'created_at' | 'updated_at'> => ({
  patient_id: null,
  metric: 'focusScore',
  condition: 'drop_pct',
  threshold_value: 20,
  severity: 'warning',
  notify_email: true,
  notify_inapp: true,
  is_active: true,
  label: '',
});

export default function AlertThresholdsConfig({ patientId, patientName, onClose }: AlertThresholdsConfigProps) {
  const { user, profile } = useAuth();
  const [thresholds, setThresholds] = useState<AlertThreshold[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getAlertThresholds(user.id, patientId);
      setThresholds(data);
    } catch {
      setError('Failed to load thresholds');
    } finally {
      setLoading(false);
    }
  }, [user, patientId]);

  useEffect(() => { load(); }, [load]);

  const handleAddTemplate = (tpl: typeof DEFAULT_THRESHOLD_TEMPLATES[0]) => {
    setForm({ ...emptyForm(), ...tpl, patient_id: patientId ?? null });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    try {
      await createAlertThreshold({
        ...form,
        clinician_id: user.id,
        patient_id: patientId ?? null,
        label: form.label || METRIC_LABELS[form.metric] || form.metric,
      });
      setSuccessMsg('Threshold saved');
      setShowForm(false);
      setForm(emptyForm());
      await load();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (e: any) {
      setError(e?.message || 'Failed to save threshold');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (t: AlertThreshold) => {
    try {
      await updateAlertThreshold(t.id, { is_active: !t.is_active });
      setThresholds(prev => prev.map(x => x.id === t.id ? { ...x, is_active: !x.is_active } : x));
    } catch { /* silent */ }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAlertThreshold(id);
      setThresholds(prev => prev.filter(x => x.id !== id));
    } catch { /* silent */ }
  };

  const sev = (s: AlertSeverity) => severityStyle[s];

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#0B1628', border: '1px solid #1E3A5F' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E3A5F]/60">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.25)' }}>
            <Bell size={14} style={{ color: '#F97316' }} />
          </div>
          <div>
            <p className="text-sm font-bold text-white">Alert Thresholds</p>
            {patientName && <p className="text-[10px] text-zinc-500">Patient: {patientName}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowForm(v => !v); setForm(emptyForm()); }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all min-h-[32px]"
            style={{ background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', color: '#F97316' }}
          >
            <Plus size={12} /> New
          </button>
          {onClose && (
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors">
              <X size={14} style={{ color: '#4A7AB5' }} />
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
        {/* Success / Error */}
        {successMsg && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-emerald-400 bg-emerald-950/30 border border-emerald-800/40">
            <Check size={12} /> {successMsg}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-red-400 bg-red-950/30 border border-red-800/40">
            <AlertTriangle size={12} /> {error}
          </div>
        )}

        {/* Quick Templates */}
        {!showForm && (
          <div>
            <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Quick Templates</p>
            <div className="grid grid-cols-2 gap-2">
              {DEFAULT_THRESHOLD_TEMPLATES.slice(0, 4).map((tpl, i) => (
                <button
                  key={i}
                  onClick={() => handleAddTemplate(tpl)}
                  className="text-left px-3 py-2 rounded-lg text-xs transition-all hover:border-[#F97316]/40"
                  style={{ background: '#060E1A', border: '1px solid #1E3A5F' }}
                >
                  <span className={`text-[9px] font-bold uppercase ${sev(tpl.severity).text}`}>{tpl.severity}</span>
                  <p className="text-white font-medium mt-0.5 leading-tight">{tpl.label}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* New Threshold Form */}
        {showForm && (
          <div className="rounded-xl p-4 space-y-3" style={{ background: '#060E1A', border: '1px solid #1E3A5F' }}>
            <p className="text-xs font-bold text-white">Configure New Threshold</p>

            {/* Label */}
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Label</label>
              <input
                value={form.label ?? ''}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Focus Drop Alert"
                className="w-full bg-[#0B1628] border border-[#1E3A5F] rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-[#F97316]/50"
              />
            </div>

            {/* Metric */}
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Metric</label>
              <select
                value={form.metric}
                onChange={e => setForm(f => ({ ...f, metric: e.target.value }))}
                className="w-full bg-[#0B1628] border border-[#1E3A5F] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-[#F97316]/50"
              >
                {METRICS.map(m => (
                  <option key={m} value={m}>{METRIC_LABELS[m]}</option>
                ))}
              </select>
            </div>

            {/* Condition + Value */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Condition</label>
                <select
                  value={form.condition}
                  onChange={e => setForm(f => ({ ...f, condition: e.target.value as ThresholdCondition }))}
                  className="w-full bg-[#0B1628] border border-[#1E3A5F] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-[#F97316]/50"
                >
                  {CONDITIONS.map(c => (
                    <option key={c} value={c}>{CONDITION_LABELS[c]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">
                  Value {form.condition.includes('pct') ? '(%)' : ''}
                </label>
                <input
                  type="number"
                  value={form.threshold_value}
                  onChange={e => setForm(f => ({ ...f, threshold_value: parseFloat(e.target.value) || 0 }))}
                  className="w-full bg-[#0B1628] border border-[#1E3A5F] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-[#F97316]/50"
                />
              </div>
            </div>

            {/* Severity */}
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Severity</label>
              <div className="flex gap-2">
                {SEVERITIES.map(s => (
                  <button
                    key={s}
                    onClick={() => setForm(f => ({ ...f, severity: s }))}
                    className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all border ${form.severity === s ? `${sev(s).bg} ${sev(s).text} ${sev(s).border}` : 'bg-transparent border-[#1E3A5F] text-zinc-500'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Notification channels */}
            <div className="flex gap-3">
              <button
                onClick={() => setForm(f => ({ ...f, notify_inapp: !f.notify_inapp }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${form.notify_inapp ? 'bg-sky-950/30 text-sky-400 border-sky-800/40' : 'bg-transparent text-zinc-500 border-[#1E3A5F]'}`}
              >
                <Bell size={11} /> In-App
              </button>
              <button
                onClick={() => setForm(f => ({ ...f, notify_email: !f.notify_email }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${form.notify_email ? 'bg-violet-950/30 text-violet-400 border-violet-800/40' : 'bg-transparent text-zinc-500 border-[#1E3A5F]'}`}
              >
                <Mail size={11} /> Email
              </button>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all min-h-[36px]"
                style={{ background: saving ? 'rgba(249,115,22,0.1)' : 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.35)', color: '#F97316' }}
              >
                {saving ? <span className="w-3 h-3 border border-[#F97316]/40 border-t-[#F97316] rounded-full animate-spin" /> : <Save size={12} />}
                {saving ? 'Saving…' : 'Save Threshold'}
              </button>
              <button
                onClick={() => { setShowForm(false); setForm(emptyForm()); }}
                className="px-3 py-2 rounded-lg text-xs text-zinc-400 hover:text-white border border-[#1E3A5F] hover:border-zinc-600 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Existing Thresholds */}
        <div>
          <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
            Active Thresholds ({thresholds.length})
          </p>
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <span className="w-5 h-5 border border-[#F97316]/30 border-t-[#F97316] rounded-full animate-spin" />
            </div>
          ) : thresholds.length === 0 ? (
            <div className="text-center py-6 text-zinc-600 text-xs">
              No thresholds configured. Use a template or create a custom one.
            </div>
          ) : (
            <div className="space-y-2">
              {thresholds.map(t => {
                const s = sev(t.severity);
                const isExpanded = expandedId === t.id;
                return (
                  <div
                    key={t.id}
                    className={`rounded-xl overflow-hidden transition-all ${!t.is_active ? 'opacity-50' : ''}`}
                    style={{ background: '#060E1A', border: `1px solid ${isExpanded ? '#1E3A5F' : '#1E3A5F'}` }}
                  >
                    <div
                      className="flex items-center gap-2 px-3 py-2.5 cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : t.id)}
                    >
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${s.bg} ${s.text} ${s.border} border`}>
                        {t.severity}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-white truncate">{t.label || METRIC_LABELS[t.metric]}</p>
                        <p className="text-[10px] text-zinc-500 truncate">
                          {METRIC_LABELS[t.metric]} {CONDITION_LABELS[t.condition].toLowerCase()} {t.threshold_value}{t.condition.includes('pct') ? '%' : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {t.notify_inapp && <Bell size={10} className="text-sky-500" />}
                        {t.notify_email && <Mail size={10} className="text-violet-500" />}
                        <button
                          onClick={e => { e.stopPropagation(); handleToggle(t); }}
                          className="p-1 rounded hover:bg-white/5 transition-colors"
                        >
                          {t.is_active
                            ? <ToggleRight size={16} className="text-emerald-400" />
                            : <ToggleLeft size={16} className="text-zinc-600" />}
                        </button>
                        {isExpanded ? <ChevronUp size={12} className="text-zinc-500" /> : <ChevronDown size={12} className="text-zinc-500" />}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-[#1E3A5F]/40 pt-2">
                        <div className="grid grid-cols-2 gap-2 text-[10px] text-zinc-400 mb-2">
                          <span>Condition: <span className="text-white">{CONDITION_LABELS[t.condition]}</span></span>
                          <span>Value: <span className="text-white">{t.threshold_value}{t.condition.includes('pct') ? '%' : ''}</span></span>
                          <span>In-App: <span className={t.notify_inapp ? 'text-sky-400' : 'text-zinc-600'}>{t.notify_inapp ? 'On' : 'Off'}</span></span>
                          <span>Email: <span className={t.notify_email ? 'text-violet-400' : 'text-zinc-600'}>{t.notify_email ? 'On' : 'Off'}</span></span>
                        </div>
                        <button
                          onClick={() => handleDelete(t.id)}
                          className="flex items-center gap-1 text-[10px] text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 size={10} /> Delete threshold
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
